import React, { useEffect, useState } from "react";
import { MembershipPlanService } from "./MembershipPlanService";

/**
 * Plans the user can see for their network.
 *
 * The page previously carried a large hardcoded `responseData` mock beside the
 * real call, rendered nothing at all while the request was in flight or when it
 * failed (an empty blue page with just the heading), and its "Renew" button was
 * made unclickable with `pointerEvents: none` while still calling `alert()`.
 * Plan changes are not self-serve here, so the button says so instead.
 */

const PAGE: React.CSSProperties = {
  background: "#313ca6",
  minHeight: "100vh",
  padding: "40px",
  color: "#fff",
};

const MembershipPlan: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    getMembershipPlans();
  }, []);

  const getMembershipPlans = async () => {
    setIsLoading(true);
    setError("");
    const resp: any = await MembershipPlanService.getMembershipPlan({});

    // baseapp wraps the superadmin API's own envelope, so the plans arrive at
    // result[0].result. Accept the unwrapped shape too rather than render a
    // blank page if that ever changes.
    const payload = resp?.result?.[0];
    const list = Array.isArray(payload?.result)
      ? payload.result
      : Array.isArray(resp?.result)
      ? resp.result
      : [];

    if (resp?.isSuccess) {
      setPlans(list);
    } else {
      setError(resp?.message || "We could not load the plans. Please try again.");
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div style={PAGE}>
        <h1 style={{ textAlign: "center" }}>Upgrade your plan</h1>
        <p style={{ textAlign: "center" }}>Loading plans…</p>
      </div>
    );
  }

  if (error || plans.length === 0) {
    return (
      <div style={PAGE}>
        <h1 style={{ textAlign: "center", marginBottom: "20px" }}>Upgrade your plan</h1>
        <p style={{ textAlign: "center" }}>
          {error || "No plans are available for your network yet."}
        </p>
        <div style={{ textAlign: "center" }}>
          <button
            onClick={getMembershipPlans}
            style={{
              background: "#098689",
              color: "#fff",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "16px",
              cursor: "pointer",
              marginTop: "15px",
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <h1 style={{ textAlign: "center", marginBottom: "30px" }}>Upgrade your plan</h1>
      <div
        style={{
          display: "flex",
          gap: "20px",
          padding: "10px",
          overflowX: "auto",
        }}
      >
        {plans.map((plan: any) => (
          <div
            key={plan.id}
            style={{
              background: "#313ca6",
              borderRadius: "12px",
              padding: "20px",
              width: "300px",
              flex: "0 0 300px",
              textAlign: "center",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.5)",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            <h2>{plan.name}</h2>
            <p style={{ fontSize: "24px", fontWeight: "bold", color: "#098689" }}>
              {plan.price} {plan.currency}/{plan.billing_cycle}
            </p>
            <p style={{ opacity: 0.7, fontSize: "13px", margin: "15px 0" }}>
              Contact your network administrator to change plan.
            </p>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                textAlign: "left",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {plan?.active_features?.map((feature: any) => (
                <li key={`a-${feature.id}`} style={{ margin: "6px 0" }}>
                  ✅ {feature.name}
                </li>
              ))}
              {plan?.inactive_features?.map((feature: any) => (
                <li key={`i-${feature.id}`} style={{ margin: "6px 0", opacity: 0.5 }}>
                  ❌ {feature.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MembershipPlan;
